import { jsPDF } from 'jspdf';
import axiosInstance from '../../../../axiosInstance';
import { message } from 'antd';

const NAVY = [23, 43, 77];          // header/footer background
const BRAND_RED = [200, 30, 45];    // accent line
const LIGHT_GREY = [246, 247, 249]; // zebra stripe
const BORDER_GREY = [210, 213, 219];
const TEXT_DARK = [40, 40, 40];

const fetchShopDetails = async () => {
    try {
        const response = await axiosInstance.get('/shop-details');
        if (response.status === 200) {
            return response.data.shop;
        } else {
            throw new Error('Error fetching shop details');
        }
    } catch (error) {
        console.log('Error:', error);
        return null;
    }
};

const exportToPDF = async (products, customFields = []) => {
    const tableColumn = [
        "Name", "Buying Price", "Selling Price", "Quantity", "Category", "Barcode", "Status",
        ...customFields.map((f) => f.name),
    ];

    const isLandscape = tableColumn.length > 9;

    const doc = new jsPDF({
        orientation: isLandscape ? 'landscape' : 'portrait',
        unit: 'mm',
        format: 'a4',
    });

    const shop = await fetchShopDetails();
    if (!shop) {
        console.error('Shop details could not be fetched');
        message.error('Failed to fetch shop details');
        return;
    }

    const tableRows = [];
    products.forEach((product, index) => {
        const customValues = customFields.map((field) => {
            const val = product.custom_field_values?.find((v) => v.custom_field_id === field.id);
            return String(val?.value ?? '-');
        });

        tableRows.push([
            String(`${index + 1}. ${product.name}`),
            Number(product.buying_price).toLocaleString(),
            Number(product.selling_price).toLocaleString(),
            String(product.total_quantity ?? 0),
            String(product.category?.name || '-'),
            String(product.barcode || '-'),
            String(product.status?.status || '-'),
            ...customValues,
        ]);
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const marginX = 12;
    const marginBottom = 22;

    // ── Header block ─────────────────────────────────────────
    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.setTextColor(...NAVY);
    doc.text(shop.name, pageWidth / 2, 14, { align: 'center' });

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9.5);
    doc.setTextColor(90, 90, 90);
    doc.text(`Phone: ${shop.phone || 'N/A'}`, pageWidth / 2, 20, { align: 'center' });

    doc.setDrawColor(...BRAND_RED);
    doc.setLineWidth(0.8);
    doc.line(marginX, 24, pageWidth - marginX, 24);
    doc.setLineWidth(0.1);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.setTextColor(...NAVY);
    doc.text("Product List", pageWidth / 2, 33, { align: 'center' });

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(120, 120, 120);
    const generatedOn = new Date().toLocaleString();
    doc.text(`Generated: ${generatedOn}`, pageWidth - marginX, 33, { align: 'right' });
    doc.text(`Total items: ${products.length}`, marginX, 33);

    // ── Table setup ───────────────────────────────────────────
    const startX = marginX;
    const startY = 42;
    const usableWidth = pageWidth - marginX * 2;
    const numRightAligned = [1, 2]; // Buying Price, Selling Price

    // Give the Name column extra room, distribute the rest evenly
    const nameWidth = usableWidth * 0.22;
    const remainingWidth = usableWidth - nameWidth;
    const otherColWidth = remainingWidth / (tableColumn.length - 1);
    const colWidths = tableColumn.map((_, i) => (i === 0 ? nameWidth : otherColWidth));
    const colX = (i) => startX + colWidths.slice(0, i).reduce((a, b) => a + b, 0);

    const lineHeight = 4;
    const cellPaddingY = 3;
    const headerFontSize = 8.5;
    const headerLineHeight = 3.6;

    // Pre-compute wrapped header lines and the header row height,
    // since it must be known before the very first drawHeaderRow() call.
    doc.setFont("helvetica", "bold");
    doc.setFontSize(headerFontSize);
    const headerCellLines = tableColumn.map((col, i) =>
        doc.splitTextToSize(String(col), colWidths[i] - 6)
    );
    const headerMaxLines = Math.max(...headerCellLines.map((l) => l.length));
    const headerRowHeight = headerMaxLines * headerLineHeight + cellPaddingY + 1;

    let currentY = startY;

    const drawHeaderRow = (y) => {
        doc.setFillColor(...NAVY);
        doc.rect(startX, y, usableWidth, headerRowHeight, 'F');

        doc.setFont("helvetica", "bold");
        doc.setFontSize(headerFontSize);
        doc.setTextColor(255, 255, 255);

        tableColumn.forEach((col, index) => {
            const x = colX(index);
            const w = colWidths[index];
            const lines = headerCellLines[index];

            const blockHeight = lines.length * headerLineHeight;
            const startTextY = y + (headerRowHeight - blockHeight) / 2 + headerLineHeight - 1;

            lines.forEach((line, lineIdx) => {
                const textY = startTextY + lineIdx * headerLineHeight;
                if (numRightAligned.includes(index)) {
                    doc.text(line, x + w - 3, textY, { align: 'right' });
                } else {
                    doc.text(line, x + 3, textY);
                }
            });

            if (index > 0) {
                doc.setDrawColor(255, 255, 255);
                doc.setLineWidth(0.1);
                doc.line(x, y + 1.5, x, y + headerRowHeight - 1.5);
            }
        });

        doc.setFont("helvetica", "normal");
    };

    drawHeaderRow(currentY);
    currentY += headerRowHeight;

    doc.setFontSize(8.5);

    tableRows.forEach((row, rowIndex) => {
        const cellLines = row.map((cell, i) =>
            doc.splitTextToSize(String(cell), colWidths[i] - 6)
        );
        const maxLines = Math.max(...cellLines.map((lines) => lines.length));
        const rowHeight = maxLines * lineHeight + cellPaddingY;

        if (currentY + rowHeight + marginBottom > pageHeight) {
            doc.addPage();
            currentY = startY;
            drawHeaderRow(currentY);
            currentY += headerRowHeight;
        }

        // Zebra stripe background
        if (rowIndex % 2 === 1) {
            doc.setFillColor(...LIGHT_GREY);
            doc.rect(startX, currentY, usableWidth, rowHeight, 'F');
        }

        // Row border (bottom rule)
        doc.setDrawColor(...BORDER_GREY);
        doc.setLineWidth(0.1);
        doc.line(startX, currentY + rowHeight, startX + usableWidth, currentY + rowHeight);

        // Column separators
        row.forEach((_, colIndex) => {
            if (colIndex > 0) {
                const x = colX(colIndex);
                doc.line(x, currentY, x, currentY + rowHeight);
            }
        });

        // Cell text
        doc.setTextColor(...TEXT_DARK);
        row.forEach((cell, colIndex) => {
            const x = colX(colIndex);
            const w = colWidths[colIndex];
            const lines = cellLines[colIndex];

            lines.forEach((line, lineIndex) => {
                const lineY = currentY + cellPaddingY + lineIndex * lineHeight;
                if (numRightAligned.includes(colIndex)) {
                    doc.text(line, x + w - 3, lineY, { align: 'right' });
                } else {
                    doc.text(line, x + 3, lineY);
                }
            });
        });

        currentY += rowHeight;
    });

    // Outer left/right border for the table body
    doc.setDrawColor(...BORDER_GREY);
    doc.line(startX, startY, startX, currentY);
    doc.line(startX + usableWidth, startY, startX + usableWidth, currentY);

    // ── Footer (navy bar on every page) ────────────────────────
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFillColor(...NAVY);
        doc.rect(0, pageHeight - 10, pageWidth, 10, 'F');
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);
        doc.setTextColor(255, 255, 255);
        doc.text(shop.name, marginX, pageHeight - 4);
        doc.text(`Page ${i} of ${pageCount}`, pageWidth - marginX, pageHeight - 4, { align: 'right' });
    }

    doc.save("product_list.pdf");
    message.success('PDF exported successfully!');
};

export default exportToPDF;